/**
 * Stat Tracker — FR-STATTRACKER-01/02, AC-STAT-001/002.
 *
 * The complete stat sheet, not the box score: every category the pipeline
 * derives for one unit, plus the tenant's own custom fields. Two views share
 * one screen:
 *
 *   - **This game** — `GET /stats/v1/tracker/{event_id}?unit=…`, live-subscribed
 *     so entry → tracker propagation needs no refresh;
 *   - **Season rollup** — `GET /stats/v1/season?season=YYYY&unit=…`, cross-game
 *     totals with per-game averages, the record and the schedule. Not pushed;
 *     the season selector refetches it.
 *
 * Everything that changes what you are looking at (unit, view, season, search,
 * sort, category) lives in the URL, so a reload on a sideline tablet comes back
 * to the same sheet and a coach can share the link.
 *
 * The page is **read-only by design** (FR-STATTRACKER-01, and what the Starter
 * tier gets): corrections are appended as new play versions on Play Entry /
 * Play Archive, never typed over an aggregate. `fanatic` cannot read the tracker
 * at all — the service answers 403 and the page says so instead of spinning.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import {
  ODST_UNITS,
  OdstSegmentedToggle,
  UNIT_TITLES,
  unitPanelId,
  unitTabId,
} from '../../components/odst-segmented-toggle';
import { PageHeader } from '../../components/page-header';
import { QuickLinksBar } from '../../components/quick-links-bar';
import { StatusPill } from '../../components/status-pill';
import { APP_VIEWS } from '../../lib/constants';
import type { HealthSocketFactory, PlayArchiveEvent } from '../../lib/live-client';
import { usePlayArchiveSubscription } from '../../lib/live-client';
import {
  patchParams as applyParamPatch,
  readEnumParam,
  readIntParam,
  readTextParam,
} from '../../lib/url-state';
import { TableExportButton } from '../integrations/export-dialog';
import { buildStatTrackerExportSource } from '../summary/summary-export';

import { SeasonGamesTable, SeasonRecordStrip } from './season-rollup-panel';
import { filterAndSortRows, StatSheet } from './stat-sheet';
import {
  SEASON_VIEWS,
  SORT_FIELDS,
  useSeasonOptions,
  useSeasonRollup,
  useTrackerFeed,
} from './use-tracker';

export interface StatTrackerPageProps {
  /** Injected by tests to drive the live channel deterministically. */
  socketFactory?: HealthSocketFactory;
}

export function StatTrackerPage({ socketFactory }: StatTrackerPageProps = {}): JSX.Element {
  const apiClient = useApiClient();
  const { persona } = useSession();
  const { activeEvent, activeEventId } = useActiveEvent();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-backed view state via the shared kit (web/src/lib/url-state.ts).
  const eventId = searchParams.get('event_id') ?? activeEventId;
  const unit = readEnumParam(searchParams, 'unit', ODST_UNITS, 'offense');
  const view = readEnumParam(searchParams, 'view', SEASON_VIEWS, 'game');
  const search = readTextParam(searchParams, 'q');
  const category = readTextParam(searchParams, 'cat');
  const sortField = readEnumParam(searchParams, 'sort', SORT_FIELDS, 'category');
  const isDescending = searchParams.get('dir') === 'desc';
  const isFanatic = persona === 'fanatic';

  const seasons = useSeasonOptions(apiClient, activeEvent?.season_year ?? null, isFanatic);
  const season = readIntParam(searchParams, 'season', { fallback: seasons.defaultSeason });

  const tracker = useTrackerFeed(apiClient, isFanatic || view !== 'game' ? null : eventId, unit);
  const rollup = useSeasonRollup(apiClient, isFanatic || view !== 'season' ? null : season, unit);

  const patchParams = useCallback(
    (patch: Record<string, string | null>): void => {
      setSearchParams(applyParamPatch(searchParams, patch), { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Stamp the resolved unit in so a bookmark and a refresh agree with the tabs.
  useEffect(() => {
    if (searchParams.get('unit') !== null) return;
    const next = new URLSearchParams(searchParams);
    next.set('unit', unit);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, unit]);

  const onLiveEvent = useCallback(
    (message: PlayArchiveEvent): void => {
      if (message.payload.event_id !== eventId) return;
      tracker.reload();
    },
    [eventId, tracker]
  );
  const live = usePlayArchiveSubscription({
    eventId: view === 'game' && !isFanatic ? eventId : null,
    onEvent: onLiveEvent,
    // Free tier (FR-LIVE-02): no socket — refetch the sheet every 5 seconds.
    onPoll: tracker.reload,
    socketFactory,
  });

  const rows = view === 'game' ? tracker.rows : rollup.rows;
  const categories = useMemo(
    () =>
      [...new Set(rows.map((row) => row.category))].sort((left, right) =>
        left.localeCompare(right)
      ),
    [rows]
  );

  if (isFanatic) {
    return (
      <section className="view stat-tracker">
        <h1 className="view__title">Stat Tracker</h1>
        <div className="banner banner--warning" role="alert" data-testid="tracker-forbidden">
          <p className="banner__title">Not available on a fanatic account</p>
          <p>
            The full stat sheet is limited to team staff. The team-aggregate box score is on the
            Game Summary page.
          </p>
          <Link className="btn btn--secondary" to={`../${APP_VIEWS.gameSummary}`} relative="path">
            Open Game Summary
          </Link>
        </div>
        <QuickLinksBar currentView={APP_VIEWS.statTracker} />
      </section>
    );
  }

  const isGameViewBlocked = view === 'game' && eventId === null;
  const activeError = view === 'game' ? tracker.error : rollup.error;
  const isLoading = view === 'game' ? tracker.isLoading : rollup.isLoading;

  return (
    <section
      className="view stat-tracker"
      data-view={view}
      data-unit={unit}
      data-refreshing={isLoading && rows.length > 0}
      data-testid="stat-tracker"
    >
      <PageHeader
        className="stat-tracker__header"
        title="Stat Tracker"
        context={
          <>
            <span className="stat-tracker__subtitle">
              {view === 'game'
                ? `${activeEvent?.name ?? 'Selected event'} · every ${UNIT_TITLES[unit].toLowerCase()} stat`
                : `${season} season · ${rollup.feed?.total_events ?? 0} games`}
            </span>
            <span className="stat-tracker__stamp">
              {view === 'game'
                ? tracker.feed === null
                  ? 'Waiting for the first snapshot…'
                  : `Updated ${new Date(tracker.feed.computed_at).toLocaleTimeString()} · ${tracker.feed.play_count} plays counted`
                : rollup.feed === null
                  ? 'No season rollup loaded yet.'
                  : `Season totals as of ${new Date(rollup.feed.computed_at).toLocaleTimeString()}`}
            </span>
          </>
        }
        meta={
          view === 'game' ? (
            <span className="stat-tracker__live" data-live={live.status}>
              <StatusPill
                tone={
                  live.status === 'live'
                    ? 'success'
                    : live.status === 'offline'
                      ? 'danger'
                      : 'neutral'
                }
              >
                {live.status === 'live'
                  ? 'Live'
                  : live.status === 'polling'
                    ? 'Auto-refresh · 5s'
                    : live.status}
              </StatusPill>
            </span>
          ) : null
        }
        actions={
          <div className="stat-tracker__view-controls">
            <label className="stat-tracker__field">
              <span>Season</span>
              <select
                value={String(season)}
                data-testid="season-selector"
                onChange={(changeEvent) =>
                  patchParams({ season: changeEvent.target.value, view: 'season' })
                }
              >
                {seasons.options.map((option) => (
                  <option key={option} value={String(option)}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <div className="stat-tracker__view-switch" role="group" aria-label="Scope">
              <button
                type="button"
                className="btn btn--ghost"
                aria-pressed={view === 'game'}
                data-testid="tracker-view-game"
                onClick={() => patchParams({ view: null })}
              >
                This game
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                aria-pressed={view === 'season'}
                data-testid="tracker-view-season"
                onClick={() => patchParams({ view: 'season', season: String(season) })}
              >
                Season rollup
              </button>
            </div>
            <TableExportButton
              isDisabled={rows.length === 0}
              source={() =>
                buildStatTrackerExportSource({
                  rows: filterAndSortRows(rows, { search, category, sortField, isDescending }),
                  unit,
                  event: activeEvent?.event_id === eventId ? activeEvent : null,
                  view,
                  season,
                })
              }
            />
          </div>
        }
      />

      <OdstSegmentedToggle
        value={unit}
        onChange={(next) => patchParams({ unit: next })}
        scope="tracker"
        counts={view === 'game' ? { [unit]: rows.length } : undefined}
        isBusy={isLoading}
      />

      <p className="stat-tracker__readonly" data-testid="tracker-readonly-note">
        Read-only view. Correct a number by editing the play on{' '}
        <Link to={`../${APP_VIEWS.playEntry}`} relative="path">
          Play Entry
        </Link>{' '}
        or{' '}
        <Link to={`../${APP_VIEWS.playArchive}`} relative="path">
          Play Archive
        </Link>
        — aggregates are always recomputed from the active play versions.
      </p>

      {view === 'season' && rollup.feed !== null ? <SeasonRecordStrip feed={rollup.feed} /> : null}

      {isGameViewBlocked ? (
        <div className="banner banner--info">
          <p className="banner__title">No event selected</p>
          <p>
            Pick a game from the event switcher above, or switch to the season rollup to see
            cross-game totals.
          </p>
        </div>
      ) : null}

      {activeError !== null ? (
        <div className="banner banner--error" role="alert">
          <p className="banner__title">Stats unavailable</p>
          <p>{activeError}</p>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => (view === 'game' ? tracker.reload() : rollup.reload())}
          >
            Try again
          </button>
        </div>
      ) : null}

      <div
        role="tabpanel"
        id={unitPanelId(unit, 'tracker')}
        aria-labelledby={unitTabId(unit, 'tracker')}
        tabIndex={-1}
      >
        <StatSheet
          rows={rows}
          categories={categories}
          search={search}
          category={category}
          sortField={sortField}
          isDescending={isDescending}
          isLoading={isLoading}
          hasPerGameColumn={view === 'season'}
          onSearchChange={(next) => patchParams({ q: next })}
          onCategoryChange={(next) => patchParams({ cat: next })}
          onClearFilters={() => patchParams({ q: null, cat: null })}
          onSortChange={(field) =>
            patchParams({
              sort: field,
              dir: sortField === field && !isDescending ? 'desc' : null,
            })
          }
        />
      </div>

      {view === 'season' && rollup.feed !== null ? <SeasonGamesTable feed={rollup.feed} /> : null}

      <QuickLinksBar currentView={APP_VIEWS.statTracker} />
    </section>
  );
}
